import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  CloudQueue as CloudIcon,
} from '@mui/icons-material';
import { useToast } from '../contexts/ToastContext';
import PageLoadingState from '../components/PageLoadingState';
import {
  DataTable,
  MetricCard,
  PageHeader,
  StatusChip,
} from '../components/primitives';
import { tasksAPI } from '../services/api';

// Humanize error_type for display (e.g. browser_profile_locked -> Browser profile locked)
const formatErrorType = (type) => {
  if (!type) return 'Scraping failure';
  const labels = {
    browser_profile_locked: 'Browser profile locked',
    timeout: 'Timeout',
    connection_error: 'Connection error',
    scraping_error: 'Scraping error',
    scraping_failure: 'Scraping failure',
  };
  return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const TaskMonitoring = () => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [taskStats, setTaskStats] = useState(null);
  const [activeTasks, setActiveTasks] = useState([]);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [scrapingStatus, setScrapingStatus] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [scrapingSchedule, setScrapingSchedule] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(null);

  const ignoreAdminDenied = (error) => {
    if (error?.response?.status === 403) {
      return null;
    }
    throw error;
  };

  const fetchAllData = async () => {
    try {
      setRefreshing(true);
      const [stats, active, scheduled, scraping, activity, schedule] =
        await Promise.all([
          tasksAPI.getTaskStats().catch(ignoreAdminDenied),
          tasksAPI.getActiveTasks().catch(ignoreAdminDenied),
          tasksAPI.getScheduledTasks().catch(ignoreAdminDenied),
          tasksAPI.getScrapingStatus(),
          tasksAPI.getRecentScrapingActivity(10),
          tasksAPI.getScrapingSchedule(),
        ]);

      setTaskStats(stats);
      setActiveTasks(active?.active_tasks || []);
      setScheduledTasks(scheduled?.scheduled_tasks || []);
      setScrapingStatus(scraping);
      setRecentActivity(activity.recent_activity || []);
      setScrapingSchedule(schedule);
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
      showError('Failed to load monitoring data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllData();

    // Auto-refresh every 5 seconds if enabled
    if (autoRefresh) {
      const interval = setInterval(fetchAllData, 5000);
      setRefreshInterval(interval);
      return () => clearInterval(interval);
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [autoRefresh]);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return <PageLoadingState minHeight={400} message="Loading task monitoring…" />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Task Monitoring"
        subtitle="Monitor Celery workers, active tasks, and scraping status"
        icon={<CloudIcon sx={{ fontSize: 28 }} />}
        actions={
          <>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchAllData}
              disabled={refreshing}
              variant="outlined"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Chip
              label={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              color={autoRefresh ? 'success' : 'default'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              clickable
            />
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        Campaign create guide (what it is, 3-step flow, AI estimates):{' '}
        <Button
          component={RouterLink}
          to="/docs/campaigns"
          size="small"
          sx={{ textTransform: 'none', verticalAlign: 'baseline' }}
        >
          Operations → Campaigns guide
        </Button>
      </Alert>

      {!taskStats && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Worker queue details are limited to admin accounts. Scraping status
          below reflects only your clients&apos; competitors.
        </Alert>
      )}

      {/* Worker Status */}
      {taskStats && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Worker Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <MetricCard
                title="Workers"
                value={taskStats.workers?.length || 0}
                subtitle={taskStats.workers?.length > 0 ? 'Online' : 'Offline'}
                icon={<PlayIcon />}
                accentColor="#58a6ff"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCard
                title="Active Tasks"
                value={taskStats.active_tasks_count || 0}
                subtitle="Currently running"
                icon={<ScheduleIcon />}
                accentColor="#1f6feb"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCard
                title="Scheduled Tasks"
                value={taskStats.scheduled_tasks_count || 0}
                subtitle="Queued tasks"
                icon={<TrendingUpIcon />}
                accentColor="#d29922"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCard
                title="Active Competitors"
                value={scrapingStatus ? scrapingStatus.active_competitors || 0 : '—'}
                subtitle={
                  scrapingStatus
                    ? `${scrapingStatus.scraped_last_24h || 0} scraped in 24h`
                    : 'Loading…'
                }
                icon={<BusinessIcon />}
                accentColor="#238636"
                loading={!scrapingStatus}
              />
            </Grid>
          </Grid>
          {taskStats.error && (
            <Alert severity="warning" sx={{ mt: 2, fontSize: '0.75rem' }}>
              {taskStats.error}
            </Alert>
          )}
        </Box>
      )}

      {/* Scraping Status Summary */}
      {scrapingStatus && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Scraping Status Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <MetricCard
                title="Total Competitors"
                value={scrapingStatus.total_competitors}
                accentColor="#58a6ff"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCard
                title="Scraped (24h)"
                value={scrapingStatus.scraped_last_24h}
                accentColor="#238636"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCard
                title="Stale Competitors"
                value={scrapingStatus.stale_competitors}
                accentColor="#d29922"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCard
                title="Total Errors"
                value={scrapingStatus.total_errors}
                accentColor="#da3633"
              />
            </Grid>
          </Grid>

          {scrapingStatus.content_strategy_pipeline && (
            <Card variant="outlined" sx={{ mt: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    Content Strategy pipeline
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/reports"
                    size="small"
                    variant="outlined"
                  >
                    Open Reports → Content Strategy
                  </Button>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Trace: scrape → computation (content breakdown) → render
                  (Reports tab). Class A mix uses scrape rows immediately;
                  enrichment runs every 10 minutes and after each successful
                  scrape.
                </Typography>
                <Grid container spacing={2}>
                  {(scrapingStatus.content_strategy_pipeline.stages || []).map(
                    (stage) => (
                      <Grid item xs={12} sm={4} key={stage.id}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                          <Chip
                            label={stage.label}
                            size="small"
                            sx={{ mb: 1 }}
                            color={
                              stage.id === 'scrape'
                                ? 'primary'
                                : stage.id === 'computation'
                                  ? 'warning'
                                  : 'success'
                            }
                            variant="outlined"
                          />
                          <Typography variant="body2" fontWeight="medium">
                            {stage.metric ?? 0}{' '}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              {stage.metric_label}
                            </Typography>
                          </Typography>
                          {typeof stage.pending === 'number' && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {stage.pending} pending enrichment
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            {stage.description}
                          </Typography>
                        </Paper>
                      </Grid>
                    )
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {scrapingStatus.recent_errors && scrapingStatus.recent_errors.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Recent Errors
              </Typography>
              <List>
                {scrapingStatus.recent_errors.slice(0, 5).map((error) => (
                  <ListItem key={error.id}>
                    <ListItemIcon>
                      <ErrorIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${error.platform} - ${formatErrorType(error.error_type)}`}
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {error.error_message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(error.occurred_at)}
                          </Typography>
                        </>
                      }
                    />
                    <Chip
                      label={error.error_severity}
                      size="small"
                      color={error.error_severity === 'critical' ? 'error' : 'warning'}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      )}

      {/* Active Tasks */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Active Tasks
          </Typography>
          {activeTasks.length === 0 ? (
            <Alert severity="info">No active tasks</Alert>
          ) : (
            <DataTable empty={false}>
                <TableHead>
                  <TableRow>
                    <TableCell>Task</TableCell>
                    <TableCell>Worker</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeTasks.map((task) => (
                    <TableRow key={task.task_id}>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {task.name || task.task_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={task.worker} size="small" />
                      </TableCell>
                      <TableCell>
                        <StatusChip label="Running" color="info" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
            </DataTable>
          )}
        </CardContent>
      </Card>

      {/* Scheduled Tasks */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scheduled Tasks
          </Typography>
          {scheduledTasks.length === 0 ? (
            <Alert severity="info">No scheduled tasks</Alert>
          ) : (
            <DataTable empty={false}>
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>ETA</TableCell>
                  <TableCell>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduledTasks.map((task) => (
                  <TableRow key={task.task_id}>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {task.name || task.task_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {task.eta ? formatTime(task.eta) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip label={task.priority || 'Normal'} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          )}
        </CardContent>
      </Card>

      {/* Scraping Schedule */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Scraping Schedule</Typography>
            <Tooltip title="Shows when competitors will be scraped using staggered scheduling">
              <InfoIcon color="action" />
            </Tooltip>
          </Box>
          
          {scrapingSchedule && scrapingSchedule.periodic_tasks && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Periodic Tasks
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {scrapingSchedule.periodic_tasks.map((task, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {task.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.schedule}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {task.description}
                      </Typography>
                      {task.pipeline_stage && (
                        <Chip
                          label={task.pipeline_stage}
                          size="small"
                          sx={{ mt: 1 }}
                          variant="outlined"
                        />
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              
              {scrapingSchedule.content_strategy_pipeline && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Content Strategy flow:{' '}
                  {scrapingSchedule.content_strategy_pipeline.flow}
                </Alert>
              )}

              {scrapingSchedule.schedule_info && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Staggered Schedule Info
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Total Competitors</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {scrapingSchedule.schedule_info.total_competitors}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Stagger Interval</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {scrapingSchedule.schedule_info.stagger_interval_minutes} min
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Platform Spacing</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {scrapingSchedule.schedule_info.platform_spacing_minutes} min
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Total Duration</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        ~{scrapingSchedule.schedule_info.estimated_completion_hours}h
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
          
          {scrapingSchedule && scrapingSchedule.schedule && scrapingSchedule.schedule.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Next Scheduled Scrapes (Next 24 Hours)
              </Typography>
              <DataTable empty={false}>
                <TableHead>
                  <TableRow>
                    <TableCell>Competitor</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>Platforms</TableCell>
                    <TableCell>Delay</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scrapingSchedule.schedule.slice(0, 10).map((item) => (
                    <TableRow key={item.competitor_id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.competitor_name}
                        </Typography>
                        {item.client_name && (
                          <Typography variant="caption" color="text.secondary">
                            {item.client_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatTime(item.start_time)}>
                          <Typography variant="body2">
                            {formatTime(item.start_time)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {item.platforms.map((platform) => (
                            <Tooltip
                              key={platform.platform}
                              title={`Scheduled: ${formatTime(platform.scheduled_time)}`}
                            >
                              <Chip
                                label={platform.platform}
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          +{item.start_delay_minutes} min
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
              {scrapingSchedule.schedule.length > 10 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Showing first 10 of {scrapingSchedule.schedule.length} scheduled competitors
                </Typography>
              )}
            </Box>
          ) : (
            <Alert severity="info">No scraping schedule available</Alert>
          )}
        </CardContent>
      </Card>

      {/* Recent Scraping Activity */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Scraping Activity
          </Typography>
          {recentActivity.length === 0 ? (
            <Alert severity="info">No recent scraping activity</Alert>
          ) : (
            <DataTable empty={false}>
              <TableHead>
                <TableRow>
                  <TableCell>Competitor</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>Last Scraped</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentActivity.map((activity) => (
                  <TableRow key={activity.competitor_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {activity.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={activity.industry} size="small" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatTime(activity.last_scraped)}>
                        <Typography variant="body2">
                          {formatRelativeTime(activity.last_scraped)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <StatusChip label="Scraped" color="success" variant="filled" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TaskMonitoring;










